import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Search, X, MessageSquare, Bell, HandCoins, MessageCircleReply, VolumeX, Ban, ChevronRight, Info } from "lucide-react";
import { confirmDialog, showError, showSuccess, notifyApiError } from "../../../components/alertas";
import { CountryPhoneSelector } from "../../../components/CountryPhoneSelector";
import 'flag-icons/css/flag-icons.min.css';
import { COUNTRIES, normalizePhone } from "../../../services/countries/countryData";
import { detectCountryFromPhone } from "../../../services/countries/phoneNormalizer";
import ClientService from "../../../services/client/ClientService";
import Sheet from "../../../components/sheet/Sheet";
import useMediaQuery from "../../../hooks/useMediaQuery";
import logger from "../../../utils/logger";
import BotAssistantCard from "./BotAssistantCard";
import "./bot.css";

const DEFAULT_MINUTES_MSG = "⏰ Hola {{nombre}}, tu cita de {{servicio}} es en {{minutes}} minutos a las {{time}}. ¡No olvides venir! 😊";
const DEFAULT_DAYS_MSG = "📅 Hola {{nombre}}, te recordamos tu cita de {{servicio}} en {{days}} día(s) a las {{time}}. ¡Te esperamos con gusto! 🎉";
const SYSTEM_TRIGGERS = ["agendar cita", "cancelar cita", "cambiar cita"];
const PREVIEW = 5; // filas visibles en la tarjeta; el resto se ve en el sheet

const triggerText = (item) => (Array.isArray(item?.trigger) ? item.trigger.join(", ") : item?.trigger || "");
const isSystemResponse = (item) => { const t = triggerText(item).toLowerCase(); return SYSTEM_TRIGGERS.some((s) => t.includes(s)); };
const flagFor = (number, country) => COUNTRIES.find((c) => c.code === (country || detectCountryFromPhone(number) || "MX"));

/**
 * Bot de WhatsApp: mensajes, agendado por chat, anticipos, recordatorios, respuestas
 * por palabra clave y listas de números. Cada bloque es una tarjeta con su propio
 * guardado; altas y ediciones en sheet. Las anclas data-tour se conservan.
 */
const BotResponsesTab = ({ handleUpdateClient, clientData, checkSubscriptionLimits, refetchClientData, canEditAssistant = true }) => {
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const side = isDesktop ? "right" : "bottom";

  const [botResponses, setBotResponses] = useState([]);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [whatsappBookingEnabled, setWhatsappBookingEnabled] = useState(true);
  const [ignoredNumbersList, setIgnoredNumbersList] = useState([]);
  const [blacklistNumbers, setBlacklistNumbers] = useState([]);
  const [advanceInstructions, setAdvanceInstructions] = useState("");
  const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [minutesBefore, setMinutesBefore] = useState(15);
  const [minutesReminderMessage, setMinutesReminderMessage] = useState(DEFAULT_MINUTES_MSG);
  const [minDaysForReminder, setMinDaysForReminder] = useState(1);
  const [daysReminderMessage, setDaysReminderMessage] = useState(DEFAULT_DAYS_MSG);

  // Sheets
  const [responseSheet, setResponseSheet] = useState(null); // { index: number|null, trigger, response }
  const [numberSheet, setNumberSheet] = useState(null); // alta: 'ignored' | 'blacklist'
  const [listSheet, setListSheet] = useState(null); // lista completa: 'ignored' | 'blacklist'

  const buildBotConfig = (overrides = {}) => ({
    reminders: { enabled: remindersEnabled, minutesBefore, minutesReminderMessage, minDaysForReminder, daysReminderMessage },
    responses: botResponses,
    ignoredNumbers: ignoredNumbersList,
    blacklist: { numbers: blacklistNumbers },
    confirmationMessage,
    welcomeMessage,
    whatsappBookingEnabled,
    ...overrides,
  });
  const saveBotConfig = (message = "Cambios guardados", overrides = {}) => handleUpdateClient({ botConfig: buildBotConfig(overrides) }, message);

  const savePaymentSettings = async () => {
    if (!clientData?.id) return;
    setSavingPaymentSettings(true);
    try {
      await ClientService.updatePaymentSettings(clientData.id, { advanceInstructions });
      showSuccess("Guardado", "Las instrucciones de anticipo se guardaron correctamente.");
    } catch (error) {
      logger.error("Error guardando paymentSettings:", error);
      notifyApiError(error, "Guardar instrucciones de anticipo");
    } finally {
      setSavingPaymentSettings(false);
      refetchClientData();
    }
  };

  useEffect(() => {
    const botConfig = clientData?.botConfig;
    if (!botConfig) return;
    setBotResponses(botConfig.responses?.map((r) => ({ ...r, trigger: triggerText(r) })) || []);
    setWelcomeMessage(botConfig.welcomeMessage || "");
    setConfirmationMessage(botConfig.confirmationMessage || "");
    setWhatsappBookingEnabled(botConfig.whatsappBookingEnabled !== false); // clientes previos sin el campo: habilitado
    if (Array.isArray(botConfig.ignoredNumbers)) setIgnoredNumbersList(botConfig.ignoredNumbers);
    if (botConfig.reminders) {
      setRemindersEnabled(botConfig.reminders.enabled || false);
      setMinutesBefore(botConfig.reminders.minutesBefore || 15);
      setMinutesReminderMessage(botConfig.reminders.minutesReminderMessage || DEFAULT_MINUTES_MSG);
      setMinDaysForReminder(botConfig.reminders.minDaysForReminder || 1);
      setDaysReminderMessage(botConfig.reminders.daysReminderMessage || DEFAULT_DAYS_MSG);
    }
    if (botConfig.blacklist?.numbers) setBlacklistNumbers(botConfig.blacklist.numbers || []);
  }, [clientData]);

  useEffect(() => { setAdvanceInstructions(clientData?.paymentSettings?.advanceInstructions || ""); }, [clientData]);

  const toggleReminders = () => {
    const v = !remindersEnabled;
    setRemindersEnabled(v);
    saveBotConfig("Recordatorios actualizados", { reminders: { enabled: v, minutesBefore, minutesReminderMessage, minDaysForReminder, daysReminderMessage } });
  };
  const toggleWhatsappBooking = () => {
    const v = !whatsappBookingEnabled;
    setWhatsappBookingEnabled(v);
    saveBotConfig(v ? "Agendado por chat activado" : "Agendado por chat desactivado", { whatsappBookingEnabled: v });
  };

  // ---- Respuestas por palabra clave ----
  const openNewResponse = () => {
    const limitCheck = checkSubscriptionLimits("responses");
    if (!limitCheck.allowed) { showError("Límite alcanzado", limitCheck.message); return; }
    setResponseSheet({ index: null, trigger: "", response: "" });
  };
  const saveResponse = () => {
    if (!responseSheet || !responseSheet.trigger.trim() || !responseSheet.response.trim()) return;
    const entry = { ...(responseSheet.index != null ? botResponses[responseSheet.index] : {}), trigger: responseSheet.trigger.trim(), response: responseSheet.response.trim(), type: "text" };
    const next = responseSheet.index == null ? [...botResponses, entry] : botResponses.map((r, i) => (i === responseSheet.index ? entry : r));
    setBotResponses(next);
    saveBotConfig(responseSheet.index == null ? "Respuesta agregada" : "Respuesta actualizada", { responses: next });
    setResponseSheet(null);
  };
  const deleteResponse = async (index) => {
    const ok = await confirmDialog({ title: "¿Eliminar esta respuesta?", text: "El bot dejará de contestar a esas palabras.", confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", icon: "warning", danger: true });
    if (!ok) return;
    const next = botResponses.filter((_, i) => i !== index);
    setBotResponses(next);
    saveBotConfig("Respuesta eliminada", { responses: next });
    setResponseSheet(null);
  };
  const customBotResponses = botResponses.map((item, originalIndex) => ({ item, originalIndex })).filter(({ item }) => !isSystemResponse(item));

  // ---- Números ignorados / bloqueados ----
  const addNumber = (kind, normalizedPhone, country, reason) => {
    if (kind === "ignored") {
      if (ignoredNumbersList.includes(normalizedPhone)) { showError("Duplicado", "Este número ya está en la lista."); return false; }
      const next = [...ignoredNumbersList, normalizedPhone];
      setIgnoredNumbersList(next);
      saveBotConfig("Número agregado a la lista de ignorados", { ignoredNumbers: next });
    } else {
      if (blacklistNumbers.some((item) => item.number === normalizedPhone)) { showError("Duplicado", "Este número ya está bloqueado."); return false; }
      const next = [...blacklistNumbers, { number: normalizedPhone, country, reason: reason?.trim() || "No especificado", addedAt: new Date().toISOString() }];
      setBlacklistNumbers(next);
      saveBotConfig("Número bloqueado", { blacklist: { numbers: next } });
    }
    return true;
  };
  const removeIgnored = (number) => {
    const next = ignoredNumbersList.filter((item) => item !== number);
    setIgnoredNumbersList(next);
    saveBotConfig("Número removido de la lista de ignorados", { ignoredNumbers: next });
  };
  const removeBlacklist = (number) => {
    const next = blacklistNumbers.filter((item) => item.number !== number);
    setBlacklistNumbers(next);
    saveBotConfig("Número desbloqueado", { blacklist: { numbers: next } });
  };

  const ignoredRows = ignoredNumbersList.map((number) => ({ number, country: null, sub: null }));
  const blockedRows = blacklistNumbers.map((item) => ({ number: item.number, country: item.country, sub: `${item.reason}${item.addedAt ? ` · desde ${new Date(item.addedAt).toLocaleDateString("es-MX")}` : ""}` }));

  return (
    <section className="cp-bot">
      <header className="cp-page-head">
        <div>
          <h2 className="cp-page-title">Bot de WhatsApp</h2>
          <p className="cp-page-sub">Lo que contesta, lo que recuerda y a quién no le habla.</p>
        </div>
      </header>

      <div className="cp-bot-grid">
        <div className="cp-bot-col">
          {/* Mensajes */}
          <article className="cp-card cp-bot-card">
            <div className="cp-card-head"><h3 className="cp-card-title"><MessageSquare size={18} /> Mensajes</h3></div>
            <label className="cp-field" data-tour="bot.greeting">
              <span>Saludo</span>
              <textarea className="cp-input" rows={3} placeholder="¡Hola! ¿En qué puedo ayudarte? 😊" value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} />
              <small>Lo primero que contesta el bot cuando alguien escribe. Es la voz de tu negocio.</small>
            </label>
            <label className="cp-field" data-tour="bot.confirmation">
              <span>Confirmación de cita</span>
              <textarea className="cp-input" rows={3} placeholder="¡Te esperamos! 😊" value={confirmationMessage} onChange={(e) => setConfirmationMessage(e.target.value)} />
              <small>Va al final de cada cita agendada o cambiada. Sirve para indicaciones: dónde estacionarse, qué traer.</small>
            </label>
            <button type="button" className="cp-btn cp-btn-primary cp-bot-save" onClick={() => saveBotConfig("Mensajes")}><Save size={16} /> Guardar mensajes</button>
          </article>

          {/* Agendado por chat */}
          <article className="cp-card cp-bot-card">
            <div className="cp-switch">
              <div>
                <div className="cp-switch-label">Agendar por el chat</div>
                <div className="cp-switch-hint">
                  {whatsappBookingEnabled
                    ? "Tus clientes agendan en la conversación, además de por tu link."
                    : "El bot solo manda el link para agendar; no lleva la reserva por el chat."}
                </div>
              </div>
              <button type="button" role="switch" aria-checked={whatsappBookingEnabled} className="cp-toggle" onClick={toggleWhatsappBooking} aria-label="Agendar por el chat" />
            </div>
          </article>

          {/* Anticipos */}
          <article className="cp-card cp-bot-card" data-tour="bot.advance">
            <div className="cp-card-head">
              <h3 className="cp-card-title"><HandCoins size={18} /> Cómo pagar el anticipo</h3>
              {advanceInstructions.trim() ? <span className="cp-pill cp-pill-ok">Listo</span> : <span className="cp-pill">Sin definir</span>}
            </div>
            <label className="cp-field">
              <textarea className="cp-input" rows={3} placeholder="Ej: Transferencia a la cuenta 1234… a nombre de…, manda tu comprobante por aquí." value={advanceInstructions} onChange={(e) => setAdvanceInstructions(e.target.value)} aria-label="Instrucciones de anticipo" />
              <small>El bot lo manda junto con la solicitud cuando un servicio pide anticipo. Sin esto no puedes activar anticipos en Servicios.</small>
            </label>
            <button type="button" className="cp-btn cp-btn-primary cp-bot-save" onClick={savePaymentSettings} disabled={savingPaymentSettings}>
              {savingPaymentSettings ? <span className="cp-spin" /> : <Save size={16} />} Guardar anticipo
            </button>
          </article>

          {/* Recordatorios */}
          <article className="cp-card cp-bot-card" data-tour="bot.reminders">
            <div className="cp-switch">
              <div>
                <div className="cp-switch-label"><Bell size={16} className="cp-bot-inline" /> Recordatorios automáticos</div>
                <div className="cp-switch-hint">Es lo que más reduce las citas perdidas.</div>
              </div>
              <button type="button" role="switch" aria-checked={remindersEnabled} className="cp-toggle" onClick={toggleReminders} aria-label="Recordatorios automáticos" />
            </div>
            {remindersEnabled && (
              <div className="cp-form cp-bot-reminders">
                <div className="cp-bot-subcard">
                  <p className="cp-eyebrow">Poco antes de la cita</p>
                  <label className="cp-field">
                    <span>Cuánto antes</span>
                    <select className="cp-input" value={minutesBefore} onChange={(e) => setMinutesBefore(parseInt(e.target.value, 10))}>
                      <option value={15}>15 minutos</option><option value={30}>30 minutos</option><option value={45}>45 minutos</option>
                      <option value={60}>1 hora</option><option value={90}>1.5 horas</option><option value={120}>2 horas</option><option value={180}>3 horas</option>
                    </select>
                  </label>
                  <label className="cp-field">
                    <span>Mensaje</span>
                    <textarea className="cp-input" rows={2} value={minutesReminderMessage} onChange={(e) => setMinutesReminderMessage(e.target.value)} />
                    <small>Puedes usar {"{{nombre}}"}, {"{{servicio}}"}, {"{{minutes}}"} y {"{{time}}"}.</small>
                  </label>
                </div>
                <div className="cp-bot-subcard">
                  <p className="cp-eyebrow">Con días de anticipación</p>
                  <label className="cp-field">
                    <span>Solo si faltan</span>
                    <select className="cp-input" value={minDaysForReminder} onChange={(e) => setMinDaysForReminder(parseInt(e.target.value, 10))}>
                      <option value={1}>1 día o más</option><option value={2}>2 días o más</option><option value={3}>3 días o más</option><option value={7}>1 semana o más</option>
                    </select>
                  </label>
                  <label className="cp-field">
                    <span>Mensaje</span>
                    <textarea className="cp-input" rows={2} value={daysReminderMessage} onChange={(e) => setDaysReminderMessage(e.target.value)} />
                    <small>Puedes usar {"{{nombre}}"}, {"{{servicio}}"}, {"{{days}}"} y {"{{time}}"}.</small>
                  </label>
                </div>
                <button type="button" className="cp-btn cp-btn-primary cp-bot-save" onClick={() => saveBotConfig("Recordatorios configurados")}><Save size={16} /> Guardar recordatorios</button>
              </div>
            )}
          </article>
        </div>

        <div className="cp-bot-col">
          {/* Respuestas por palabra clave */}
          <article className="cp-card cp-bot-card" data-tour="bot.responses">
            <div className="cp-card-head">
              <h3 className="cp-card-title"><MessageCircleReply size={18} /> Respuestas por palabra clave {customBotResponses.length > 0 && <span className="cp-count">{customBotResponses.length}</span>}</h3>
              <button type="button" className="cp-btn" onClick={openNewResponse}><Plus size={16} /> <span className="cp-bot-btnlabel">Agregar</span></button>
            </div>
            <p className="cp-muted">"precio", "ubicación", "estacionamiento"… escribe las palabras y la respuesta, y el bot contesta solo.</p>
            <div className="cp-note"><Info size={16} /><div>Agendar, cancelar y cambiar cita ya vienen en el sistema y no se editan.</div></div>
            {customBotResponses.length === 0 ? (
              <div className="cp-empty cp-bot-empty">
                <MessageCircleReply size={30} />
                <h3>Sin respuestas propias</h3>
                <p>Empieza por las preguntas que más te hacen: precios, ubicación, formas de pago.</p>
              </div>
            ) : (
              <ul className="cp-list">
                {customBotResponses.map(({ item, originalIndex }) => (
                  <li key={originalIndex}>
                    <button type="button" className="cp-row" onClick={() => setResponseSheet({ index: originalIndex, trigger: item.trigger, response: item.response })}>
                      <span className="cp-row-main">
                        <span className="cp-bot-keywords">
                          {item.trigger.split(",").map((k) => k.trim()).filter(Boolean).slice(0, 4).map((k, i) => <span key={i} className="cp-pill cp-pill-accent">{k}</span>)}
                          {item.trigger.split(",").filter((k) => k.trim()).length > 4 && <span className="cp-pill">+{item.trigger.split(",").filter((k) => k.trim()).length - 4}</span>}
                        </span>
                        <span className="cp-row-sub">{item.response}</span>
                      </span>
                      <ChevronRight size={18} className="cp-chev" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </article>

          {/* Números ignorados */}
          <article className="cp-card cp-bot-card" data-tour="bot.ignored">
            <div className="cp-card-head">
              <h3 className="cp-card-title"><VolumeX size={18} /> Ignorar: no le hables {ignoredNumbersList.length > 0 && <span className="cp-count">{ignoredNumbersList.length}</span>}</h3>
              <button type="button" className="cp-btn" onClick={() => setNumberSheet("ignored")}><Plus size={16} /> <span className="cp-bot-btnlabel">Agregar</span></button>
            </div>
            <p className="cp-muted">Tu círculo personal: tu número, tu familia, proveedores. El bot no les contesta nada por el chat, ni con el asistente. Sí pueden agendar desde tu link.</p>
            <NumberRows rows={ignoredRows} onRemove={removeIgnored} empty="Aún no hay números en esta lista." onSeeAll={() => setListSheet("ignored")} />
          </article>

          {/* Bloqueados */}
          <article className="cp-card cp-bot-card" data-tour="bot.banned">
            <div className="cp-card-head">
              <h3 className="cp-card-title"><Ban size={18} /> Bloquear: no le agendes {blacklistNumbers.length > 0 && <span className="cp-count">{blacklistNumbers.length}</span>}</h3>
              <button type="button" className="cp-btn" onClick={() => setNumberSheet("blacklist")}><Plus size={16} /> <span className="cp-bot-btnlabel">Bloquear</span></button>
            </div>
            <p className="cp-muted">Spam o clientes conflictivos. El bot les contesta dudas, pero no les agenda ni por el chat ni por tu link: les pide comunicarse directo contigo.</p>
            <NumberRows rows={blockedRows} onRemove={removeBlacklist} empty="Nadie bloqueado." onSeeAll={() => setListSheet("blacklist")} />
          </article>

          {/* Asistente con IA (add-on) */}
          <BotAssistantCard clientData={clientData} refetchClientData={refetchClientData} whatsappBookingEnabled={whatsappBookingEnabled} canEdit={canEditAssistant} side={side} />
        </div>
      </div>

      {/* Sheet: respuesta por palabra clave */}
      <Sheet open={!!responseSheet} onClose={() => setResponseSheet(null)} side={side} title={responseSheet?.index == null ? "Nueva respuesta" : "Editar respuesta"} subtitle="El bot contesta esto cuando lee alguna de las palabras" className="cp-vars"
        footer={responseSheet && (
          <div className={`cp-sheet-actions ${responseSheet.index == null ? "" : ""}`}>
            {responseSheet.index == null
              ? <button type="button" className="cp-btn" onClick={() => setResponseSheet(null)}>Cancelar</button>
              : <button type="button" className="cp-btn cp-btn-danger" onClick={() => deleteResponse(responseSheet.index)}><Trash2 size={16} /> Eliminar</button>}
            <button type="button" className="cp-btn cp-btn-primary" onClick={saveResponse} disabled={!responseSheet.trigger.trim() || !responseSheet.response.trim()}>
              {responseSheet.index == null ? <><Plus size={16} /> Agregar</> : "Guardar"}
            </button>
          </div>
        )}
      >
        {responseSheet && (
          <div className="cp-form cp-bot-sheetform">
            <label className="cp-field">
              <span>Palabras clave</span>
              <KeywordChipsInput value={responseSheet.trigger} onChange={(v) => setResponseSheet({ ...responseSheet, trigger: v })} />
              <small>Escribe una palabra y Enter (o coma) para agregarla. Basta con que el mensaje contenga una.</small>
            </label>
            <label className="cp-field">
              <span>Respuesta</span>
              <textarea className="cp-input" rows={4} placeholder="El corte cuesta $150 y la barba $100 😊" value={responseSheet.response} onChange={(e) => setResponseSheet({ ...responseSheet, response: e.target.value })} />
            </label>
          </div>
        )}
      </Sheet>

      <NumberSheet kind={numberSheet} side={side} onClose={() => setNumberSheet(null)} onAdd={addNumber} />

      {/* Sheet: lista completa con buscador */}
      <Sheet open={!!listSheet} onClose={() => setListSheet(null)} side={side}
        title={listSheet === "blacklist" ? "Bloqueados: no les agenda" : "Ignorados: no les habla"}
        subtitle={`${listSheet === "blacklist" ? blockedRows.length : ignoredRows.length} números`} className="cp-vars"
        footer={
          <div className="cp-sheet-actions is-single">
            <button type="button" className="cp-btn cp-btn-primary" onClick={() => setNumberSheet(listSheet)}><Plus size={16} /> {listSheet === "blacklist" ? "Bloquear otro" : "Agregar otro"}</button>
          </div>
        }
      >
        {listSheet && (
          <NumberList rows={listSheet === "blacklist" ? blockedRows : ignoredRows} onRemove={listSheet === "blacklist" ? removeBlacklist : removeIgnored} />
        )}
      </Sheet>
    </section>
  );
};

function NumberRow({ row, onRemove }) {
  const c = flagFor(row.number, row.country);
  return (
    <li className="cp-row cp-bot-numrow">
      {c && <span className={`fi fi-${c.code.toLowerCase()} cp-bot-flag`} aria-hidden="true" />}
      <span className="cp-row-main">
        <span className="cp-row-title">+{row.number}</span>
        {row.sub && <span className="cp-row-sub">{row.sub}</span>}
      </span>
      <button type="button" className="cp-iconbtn" onClick={() => onRemove(row.number)} aria-label="Quitar de la lista"><Trash2 size={16} /></button>
    </li>
  );
}

/** Vista previa en la tarjeta: las primeras filas y un enlace al sheet con todas. */
function NumberRows({ rows, onRemove, empty, onSeeAll }) {
  if (rows.length === 0) return <p className="cp-hint">{empty}</p>;
  const shown = rows.slice(0, PREVIEW);
  return (
    <>
      <ul className="cp-list">{shown.map((row) => <NumberRow key={row.number} row={row} onRemove={onRemove} />)}</ul>
      {rows.length > PREVIEW && (
        <button type="button" className="cp-linkbtn" onClick={onSeeAll}>Ver los {rows.length} números <ChevronRight size={15} /></button>
      )}
    </>
  );
}

/** Lista completa (sheet) con buscador por dígitos. */
function NumberList({ rows, onRemove }) {
  const [q, setQ] = useState("");
  const digits = q.replace(/\D/g, "");
  const filtered = digits ? rows.filter((r) => r.number.replace(/\D/g, "").includes(digits)) : rows;
  return (
    <div className="cp-bot-numlist">
      <div className="cp-input-wrap">
        <Search size={17} />
        <input type="search" inputMode="numeric" className="cp-input" placeholder="Buscar por número" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar número" autoComplete="off" />
        {q && <button type="button" className="cp-input-clear" onClick={() => setQ("")} aria-label="Limpiar"><X size={16} /></button>}
      </div>
      {filtered.length === 0
        ? <p className="cp-hint">Ningún número coincide con "{q}".</p>
        : <ul className="cp-list">{filtered.map((row) => <NumberRow key={row.number} row={row} onRemove={onRemove} />)}</ul>}
    </div>
  );
}

/** Palabras clave como chips removibles en vez de texto libre con comas: evita triggers
 *  vacíos por comas sobrantes (que antes hacían que la respuesta contestara cualquier
 *  mensaje). Guarda/lee el mismo string separado por coma que ya espera el backend. */
function KeywordChipsInput({ value, onChange }) {
  const words = value.split(",").map((w) => w.trim()).filter(Boolean);
  const [draft, setDraft] = useState("");

  const commit = () => {
    const w = draft.trim();
    setDraft("");
    if (!w) return;
    if (words.some((existing) => existing.toLowerCase() === w.toLowerCase())) return;
    onChange([...words, w].join(", "));
  };
  const removeAt = (i) => onChange(words.filter((_, idx) => idx !== i).join(", "));
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !draft && words.length) {
      removeAt(words.length - 1);
    }
  };

  return (
    <div className="cp-chipinput">
      {words.length > 0 && (
        <div className="cp-bot-keywords">
          {words.map((w, i) => (
            <span key={i} className="cp-pill cp-pill-accent cp-chipinput-pill">
              {w}
              <button type="button" className="cp-chipinput-remove" onClick={() => removeAt(i)} aria-label={`Quitar "${w}"`}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="cp-input"
        placeholder={words.length ? "Otra palabra…" : "precio, precios, cuánto cuesta…"}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        aria-label="Agregar palabra clave"
      />
    </div>
  );
}

/** Alta de un número (ignorado o bloqueado) con selector de país; bloqueado admite razón. */
function NumberSheet({ kind, side, onClose, onAdd }) {
  const [country, setCountry] = useState("MX");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [reason, setReason] = useState("");
  const isBlock = kind === "blacklist";

  const close = () => { setPhone(""); setPhoneError(""); setReason(""); onClose(); };
  const submit = () => {
    if (!phone.trim()) { showError("Campo vacío", "Escribe el número."); return; }
    if (phoneError) { showError("Número inválido", phoneError); return; }
    const normalized = normalizePhone(phone.trim(), country);
    if (onAdd(kind, normalized, country, reason)) {
      showSuccess(isBlock ? "Bloqueado" : "Agregado", `+${normalized}`);
      close();
    }
  };

  return (
    <Sheet open={!!kind} onClose={close} side={side} title={isBlock ? "Bloquear número" : "Ignorar número"} subtitle={isBlock ? "Le contesta, pero no le agenda" : "No le habla por el chat; sí puede agendar por tu link"} className="cp-vars"
      footer={
        <div className="cp-sheet-actions">
          <button type="button" className="cp-btn" onClick={close}>Cancelar</button>
          <button type="button" className="cp-btn cp-btn-primary" onClick={submit} disabled={!phone.trim() || !!phoneError}>{isBlock ? "Bloquear" : "Agregar"}</button>
        </div>
      }
    >
      <div className="cp-form cp-bot-sheetform">
        <div className="cp-field">
          <span>Número de WhatsApp</span>
          <CountryPhoneSelector selectedCountry={country} onCountryChange={setCountry} phoneValue={phone} onPhoneChange={setPhone} phoneError={phoneError} setPhoneError={setPhoneError} placeholder="Número" />
        </div>
        {isBlock && (
          <label className="cp-field">
            <span>Razón <small>(opcional)</small></span>
            <input type="text" className="cp-input" placeholder="Spam, no se presentó, etc." value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
        )}
      </div>
    </Sheet>
  );
}

export default BotResponsesTab;
